import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import type { Entity } from '@backstage/catalog-model';
import type { Pipeline } from '../pipeline/types';
import type { PullRequestsAnswer } from '../pullRequests/types';
import { slugQuery, useInsights, type Loaded } from '../pipeline/useInsights';
import { useServiceStatus, type ServiceStatus } from './serviceStatus';
import {
  SCORED_TYPE,
  type ServiceScorecard,
} from '../../scorecards/scorecards';
import {
  useComponentCatalog,
  type ComponentCatalog,
} from './useComponentCatalog';
import {
  isNoTeam,
  type ComponentSecurityAnswer,
} from '../../security/security';

export const PROJECT_SLUG = 'github.com/project-slug';
export const TECHDOCS_REF = 'backstage.io/techdocs-ref';

export type ComponentPageData = {
  entity: Entity;
  slug?: string;
  catalog: ComponentCatalog;
  pipeline: Loaded<Pipeline>;
  pullRequests: Loaded<PullRequestsAnswer>;
  /**
   * Ask for the pull requests again, keeping the last answer on screen until
   * the new one arrives — the Pull Requests tab does on opening.
   */
  refreshPullRequests: () => void;
  /** The status service's answer; null when it does not know the component. */
  status: Loaded<ServiceStatus | null>;
  /**
   * Its row on Scorecards — asked only of a service, the one type scored
   * (specs/scorecards.md AC-7); undefined for every other component.
   */
  scorecard?: Loaded<ServiceScorecard>;
  /**
   * Its row on the Security page — asked only of a component that names its
   * repository (specs/security-findings.md AC-9); undefined for every other.
   */
  security?: Loaded<ComponentSecurityAnswer>;
};

const Context = createContext<ComponentPageData | undefined>(undefined);

/**
 * What a component's page reads beyond the entity, asked for once and shared by
 * the header, the tab strip, the banner and the tabs — so a count on a tab and
 * the same count on the Overview come from one answer, and opening the page
 * asks GitHub, the catalog and the status service each question once
 * (specs/component-page.md).
 */
export const ComponentPageProvider = ({
  entity,
  children,
}: {
  entity: Entity;
  children: ReactNode;
}) => {
  const slug = entity.metadata.annotations?.[PROJECT_SLUG];
  const catalog = useComponentCatalog(entity);
  // Asked again while a deployment or the default branch's run is going, as
  // the CI/CD tab does, so the facts bar never contradicts that tab mid-run.
  const pipeline = useInsights<Pipeline>(
    slug ? `/pipeline?${slugQuery(slug)}` : undefined,
    {
      refreshWhile: p =>
        [p.latestDeployment, p.latestOnDefaultBranch].some(
          r => !!r?.status && r.status !== 'completed',
        ),
    },
  );
  // Asked again whenever the Pull Requests tab opens, so a page left open
  // does not show a reviewer an old list — and the counts move with it.
  const [pullRequestsAsked, setPullRequestsAsked] = useState(0);
  const pullRequests = useInsights<PullRequestsAnswer>(
    slug ? `/pull-requests?${slugQuery(slug)}` : undefined,
    { refreshKey: String(pullRequestsAsked) },
  );
  const refreshPullRequests = useCallback(
    () => setPullRequestsAsked(n => n + 1),
    [],
  );
  // The status service knows a service by its manifest id, which is the
  // component's catalog name, and says which repository it came from — so a
  // namesake from another repository is not taken for this component
  // (docs/status-probes.md).
  const status = useServiceStatus(entity.metadata.name, slug);
  // The score is the backend's, the one Scorecards shows — never computed
  // here, where it could come out different from the page's row.
  const scored = String(entity.spec?.type).toLowerCase() === SCORED_TYPE;
  const scorecard = useInsights<ServiceScorecard>(
    scored
      ? `/scorecards/${encodeURIComponent(
          entity.metadata.namespace ?? 'default',
        )}/${encodeURIComponent(entity.metadata.name)}`
      : undefined,
  );
  // The security findings are the backend's, the row the Security page shows,
  // so the tab, its count and the facts bar cannot count differently (AC-11).
  const security = useInsights<ComponentSecurityAnswer>(
    slug
      ? `/security/${encodeURIComponent(
          entity.metadata.namespace ?? 'default',
        )}/${encodeURIComponent(entity.metadata.name)}`
      : undefined,
    { refused: isNoTeam },
  );
  return (
    <Context.Provider
      value={{
        entity,
        slug,
        catalog,
        pipeline,
        pullRequests,
        refreshPullRequests,
        status,
        scorecard: scored ? scorecard : undefined,
        security: slug ? security : undefined,
      }}
    >
      {children}
    </Context.Provider>
  );
};

/**
 * The page's answers when inside a component's page, undefined elsewhere — for
 * a tab an API's page carries too, which then asks for itself.
 */
export function useComponentPageIfAny(): ComponentPageData | undefined {
  return useContext(Context);
}

export function useComponentPage(): ComponentPageData {
  const value = useContext(Context);
  if (!value) {
    throw new Error(
      'useComponentPage must be used inside ComponentPageProvider',
    );
  }
  return value;
}
